import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@ui/app/store';
import { SortOption } from '@packages/types';

// ==================== Base Selectors ====================
export const selectCalculationsUI = (state: RootState) => state.calculationsUI;

// ==================== Shared Selectors ====================
export const selectActiveTab = (state: RootState) => state.calculationsUI.activeTab;
export const selectViewMode = (state: RootState) => state.calculationsUI.viewMode;

// ==================== Calculations Tab Selectors ====================
export const selectCalculationsTab = (state: RootState) => state.calculationsUI.calculationsTab;

export const selectSelectedCategories = (state: RootState) => state.calculationsUI.calculationsTab.selectedCategories;
export const selectCategoriesInitialized = (state: RootState) =>
    state.calculationsUI.calculationsTab.categoriesInitialized;
export const selectSearchValue = (state: RootState) => state.calculationsUI.calculationsTab.searchValue;
export const selectSortValue = (state: RootState): SortOption =>
    state.calculationsUI.calculationsTab.sortValue as SortOption;
export const selectPage = (state: RootState) => state.calculationsUI.calculationsTab.currentPage;
export const selectPageSize = (state: RootState) => state.calculationsUI.calculationsTab.pageSize;

export const selectCalculationsFilters = createSelector([selectCalculationsTab], calculationsTab => ({
    selectedCategories: calculationsTab.selectedCategories,
    searchValue: calculationsTab.searchValue,
    sortValue: calculationsTab.sortValue,
}));

export const selectCalculationsPagination = createSelector([selectCalculationsTab], calculationsTab => ({
    currentPage: calculationsTab.currentPage,
    pageSize: calculationsTab.pageSize,
}));

export const selectSortParams = createSelector([selectSortValue], sortValue => {
    switch (sortValue) {
        case SortOption.NAME_ASC:
            return { sortBy: 'name' as const, sortOrder: 'asc' as const };
        case SortOption.NAME_DESC:
            return { sortBy: 'name' as const, sortOrder: 'desc' as const };
        case SortOption.CATEGORY:
            return { sortBy: 'category' as const, sortOrder: 'asc' as const };
        default:
            return { sortBy: 'name' as const, sortOrder: 'asc' as const };
    }
});

// ==================== Metrics Tab Selectors ====================
export const selectMetricsTab = (state: RootState) => state.calculationsUI.metricsTab;

export const selectSelectedMetrics = (state: RootState) => state.calculationsUI.metricsTab.selectedMetrics;
export const selectSelectedCohort = (state: RootState) => state.calculationsUI.metricsTab.selectedCohort;
export const selectMetricsSearchValue = (state: RootState) => state.calculationsUI.metricsTab.searchValue;
export const selectMetricsSortField = (state: RootState) => state.calculationsUI.metricsTab.sortField;
export const selectMetricsSortOrder = (state: RootState) => state.calculationsUI.metricsTab.sortOrder;
export const selectMetricsPage = (state: RootState) => state.calculationsUI.metricsTab.currentPage;
export const selectMetricsPageSize = (state: RootState) => state.calculationsUI.metricsTab.pageSize;

export const selectMetricsPagination = createSelector([selectMetricsTab], metricsTab => ({
    currentPage: metricsTab.currentPage,
    pageSize: metricsTab.pageSize,
}));

export const selectMetricsFilters = createSelector([selectMetricsTab], metricsTab => ({
    selectedMetrics: metricsTab.selectedMetrics,
    selectedCohort: metricsTab.selectedCohort,
    searchValue: metricsTab.searchValue,
    sortField: metricsTab.sortField,
    sortOrder: metricsTab.sortOrder,
}));

// ==================== Details Modal Selectors ====================
export const selectDetailsModal = (state: RootState) => state.calculationsUI.detailsModal;

export const selectDetailsModalCohort = (state: RootState) => state.calculationsUI.detailsModal.selectedCohort;
